import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import TempStart from './TempStart';
import AppShell, { useShellUser } from '../components/AppShell';
import '../styles/Rewards.css';

const EXPERIENCE_PREVIEW = 3;
const MERCH_PREVIEW = 3;
const RAFFLE_PREVIEW = 4;
const MERCH_TYPES = { merchandise: true, apparel: true, collectibles: true, gift_card: true };
const SOON_MS = 3 * 24 * 60 * 60 * 1000;

function formatPoints(value) {
  return (Number(value) || 0).toLocaleString('en-US');
}

function listOf(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function parseRewardDate(value) {
  if (!value) return null;
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (parts) return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseDateEndOfDay(value) {
  const date = parseRewardDate(value);
  if (!date) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) date.setHours(23, 59, 59, 999);
  return date;
}

function firstValue(obj, keys) {
  if (!obj) return null;
  for (let i = 0; i < keys.length; i += 1) {
    const value = obj[keys[i]];
    if (value != null && String(value).length) return value;
  }
  return null;
}

function infoBlocks(reward) {
  return [reward?.raffle_info, reward?.auction_info].filter(Boolean);
}

function rewardStart(reward) {
  const blocks = infoBlocks(reward);
  for (let i = 0; i < blocks.length; i += 1) {
    const found = firstValue(blocks[i], ['start_date', 'startDate']);
    if (found) return found;
  }
  return null;
}

function rewardEnd(reward) {
  const blocks = infoBlocks(reward);
  for (let i = 0; i < blocks.length; i += 1) {
    const found = firstValue(blocks[i], ['drawing_date', 'drawingDate', 'end_date', 'endDate']);
    if (found) return found;
  }
  return null;
}

function windowOpen(reward, now) {
  const start = parseRewardDate(rewardStart(reward));
  if (start && start.getTime() > now) return false;
  const end = parseDateEndOfDay(rewardEnd(reward));
  if (end && end.getTime() < now) return false;
  return true;
}

function rewardImage(reward) {
  const direct = reward?.image_url || reward?.imageUrl || '';
  if (direct) return direct;
  const images = reward?.images || [];
  const primary = images.find((image) => image && (image.is_primary || image.isPrimary)) || images[0];
  if (!primary) return '';
  if (typeof primary === 'string') return primary;
  return primary.image_path || primary.imagePath || primary.url || '';
}

function milestoneImage(milestone) {
  if (Array.isArray(milestone?.images) && milestone.images[0]) {
    const first = milestone.images[0];
    if (typeof first === 'string') return first;
  }
  return milestone?.imageUrl || milestone?.image_url || '';
}

function milestoneCost(milestone) {
  return Number(milestone?.pointsCost ?? milestone?.points_cost) || 0;
}

function periodEndMs(raw, dateOnly) {
  const value = raw || dateOnly;
  if (!value) return null;
  const text = String(value);
  if (text.includes('T')) {
    const parsed = new Date(text).getTime();
    if (Number.isFinite(parsed)) return parsed;
  }
  const datePart = text.split('T')[0];
  const parsed = new Date(`${datePart}T16:00:00`).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function compactLeft(end, now) {
  if (!end) return '';
  const delta = end - now;
  if (delta <= 0) return 'Ended';
  const days = Math.floor(delta / 86400000);
  const hours = Math.floor((delta % 86400000) / 3600000);
  const minutes = Math.floor((delta % 3600000) / 60000);
  const seconds = Math.floor((delta % 60000) / 1000);
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  if (minutes > 0) return `${minutes}m ${seconds}s left`;
  return `${seconds}s left`;
}

function unlockLabel(start, now) {
  const delta = start - now;
  if (delta <= 0) return '';
  const days = Math.floor(delta / 86400000);
  const hours = Math.floor((delta % 86400000) / 3600000);
  const minutes = Math.floor((delta % 3600000) / 60000);
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours || days) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return `Unlocks in ${parts.join(' ')}`;
}

function money(cents) {
  const value = Number(cents) / 100;
  if (!Number.isFinite(value) || value <= 0) return '';
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
}

function prizeTitle(prize, fallback) {
  return prize?.cover_title || prize?.title || prize?.resolved_reward_title || prize?.name || fallback;
}

function prizeImage(prize) {
  return prize?.cover_image_url || prize?.image_url || prize?.cover_image || prize?.image_urls?.[0] || '';
}

function prizeId(prize) {
  return prize?.catalog_reward_id || prize?.reward_id || prize?.rewardId || prize?.leaderboard_reward_id || '';
}

function redemptionType(reward) {
  let kind = String(reward?.redemption_type || reward?.redemptionType || '').toLowerCase();
  if (!kind || kind === 'general') {
    if (reward?.raffle_info?.drawing_date) kind = 'raffle';
    else if (reward?.auction_info?.end_date) kind = 'auction';
  }
  return kind;
}

function isClosed(reward, now) {
  const end = parseDateEndOfDay(rewardEnd(reward));
  return !!(end && end.getTime() < now);
}

function endsOnLabel(value) {
  const date = parseRewardDate(value);
  if (!date) return '';
  return `Ends ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function opensLabel(value) {
  const date = parseRewardDate(value);
  if (!date) return '';
  return `Opens ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

function openProps(onOpen) {
  if (!onOpen) return {};
  return {
    role: 'link',
    tabIndex: 0,
    onClick: onOpen,
    onKeyDown: (event) => {
      if (event.key === 'Enter') onOpen();
    },
  };
}

function PrizeCard({ label, title, image, status, feature, onOpen }) {
  return (
    <article className={`rewards-prize${feature ? ' is-feature' : ''}${onOpen ? ' is-link' : ''}`} {...openProps(onOpen)}>
      {image ? <img src={image} alt="" /> : <span className="rewards-prize-ph" />}
      <span className="rewards-prize-label">
        <i />
        {label}
      </span>
      {status ? <span className="rewards-prize-status">{status}</span> : null}
      <strong>{title}</strong>
    </article>
  );
}

function RewardsPage() {
  const { user, setUser } = useShellUser();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [stripeNotice, setStripeNotice] = useState(() =>
    params.get('stripe_success') === '1' ? 'success' : params.get('stripe_cancel') === '1' ? 'cancel' : ''
  );
  const [rewards, setRewards] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [earnedPoints, setEarnedPoints] = useState(0);
  const [weekly, setWeekly] = useState(null);
  const [season, setSeason] = useState(null);
  const [weekEndsAt, setWeekEndsAt] = useState(null);
  const [ready, setReady] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [showExperiences, setShowExperiences] = useState(false);
  const [showMerch, setShowMerch] = useState(false);
  const [showRaffles, setShowRaffles] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [rewardPayload, firstPayload, board, freshUser] = await Promise.all([
        apiService.getRewards().catch(() => null),
        apiService.getFirstRewards().catch(() => null),
        apiService.getLeaderboard().catch(() => null),
        apiService.fetchCurrentUser().catch(() => null),
      ]);
      if (cancelled) return;
      if (freshUser) setUser(freshUser);
      setRewards(listOf(rewardPayload));
      const earned = Number(firstPayload?.earnedPoints ?? firstPayload?.earned_points);
      setEarnedPoints(Number.isFinite(earned) ? earned : 0);
      const rows = Array.isArray(firstPayload?.milestones) ? firstPayload.milestones.slice() : [];
      rows.sort((a, b) => milestoneCost(a) - milestoneCost(b));
      setMilestones(rows.filter((item) => milestoneCost(item) > 0));
      const weeklyPrize = board?.weekly_prize || null;
      const seasonPrize = board?.overall_prize || null;
      setWeekly(prizeId(weeklyPrize) ? weeklyPrize : null);
      setSeason(prizeId(seasonPrize) ? seasonPrize : null);
      setWeekEndsAt(board?.week_ends_at || null);
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [setUser]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (params.get('stripe_success') || params.get('stripe_cancel')) {
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const availablePoints = Number(user?.current_points) || 0;

  const featured = useMemo(() => {
    return rewards.find(
      (reward) =>
        reward &&
        (reward.is_featured || reward.isFeatured) &&
        reward.is_active !== false &&
        windowOpen(reward, now)
    );
  }, [rewards, now]);

  const featuredId = featured ? String(featured.id) : '';

  const experiences = useMemo(() => {
    return rewards.filter((reward) => {
      if (!reward || String(reward.id) === featuredId || reward.is_active === false) return false;
      if (redemptionType(reward) === 'raffle') return false;
      if (isClosed(reward, now)) return false;
      return String(reward.reward_type || reward.rewardType || '').toLowerCase() === 'experience';
    });
  }, [rewards, featuredId, now]);

  const merch = useMemo(() => {
    return rewards.filter((reward) => {
      if (!reward || String(reward.id) === featuredId || reward.is_active === false) return false;
      if (redemptionType(reward) === 'raffle') return false;
      if (isClosed(reward, now)) return false;
      return !!MERCH_TYPES[String(reward.reward_type || reward.rewardType || '').toLowerCase()];
    });
  }, [rewards, featuredId, now]);

  const raffles = useMemo(() => {
    return rewards
      .filter((reward) => {
        if (!reward || String(reward.id) === featuredId || reward.is_active === false) return false;
        if (redemptionType(reward) !== 'raffle') return false;
        return !isClosed(reward, now);
      })
      .slice()
      .sort((a, b) => {
        const aStart = parseRewardDate(rewardStart(a));
        const bStart = parseRewardDate(rewardStart(b));
        const aOpen = !aStart || aStart.getTime() <= now;
        const bOpen = !bStart || bStart.getTime() <= now;
        if (aOpen !== bOpen) return aOpen ? -1 : 1;
        const aEnd = parseDateEndOfDay(rewardEnd(a));
        const bEnd = parseDateEndOfDay(rewardEnd(b));
        if (aOpen) return (aEnd?.getTime() || 0) - (bEnd?.getTime() || 0);
        return (aStart?.getTime() || 0) - (bStart?.getTime() || 0);
      });
  }, [rewards, featuredId, now]);

  const nextLockedIndex = milestones.findIndex((item) => !item.unlocked);
  const experienceCards = showExperiences ? experiences : experiences.slice(0, EXPERIENCE_PREVIEW);
  const merchCards = showMerch ? merch : merch.slice(0, MERCH_PREVIEW);
  const raffleCards = showRaffles ? raffles : raffles.slice(0, RAFFLE_PREVIEW);

  const weeklyStatus = compactLeft(
    periodEndMs(weekly?.week_end_at || weekEndsAt, weekly?.week_end_date),
    now
  );
  const seasonStatus = season?.end_date ? endsOnLabel(season.end_date) : compactLeft(periodEndMs(season?.overall_ends_at), now);
  const featuredEnd = periodEndMs(rewardEnd(featured));
  const featuredStatus = featuredEnd ? compactLeft(featuredEnd, now) : '';

  const hasCatalog = !!(featured || weekly || season || milestones.length || experiences.length || merch.length || raffles.length);

  return (
    <div className="rewards-page">
      {stripeNotice ? (
        <div className={`rewards-notice${stripeNotice === 'success' ? ' is-success' : ''}`} role="status">
          <span>
            <strong>{stripeNotice === 'success' ? 'Purchase successful!' : 'Payment canceled'}</strong>
            <span>
              {stripeNotice === 'success'
                ? 'Thank you for your purchase. Your card payment was received and your reward is being processed.'
                : 'Your card was not charged.'}
            </span>
          </span>
          <button type="button" onClick={() => setStripeNotice('')} aria-label="Dismiss">
            ×
          </button>
        </div>
      ) : null}
      {featured || weekly || season ? (
        <section className={`rewards-hero${featured ? '' : ' is-pair'}`} aria-label="Prizes">
          {featured ? (
            <PrizeCard
              feature
              label="Featured prize"
              title={featured.title}
              image={rewardImage(featured)}
              status={featuredStatus}
              onOpen={() => navigate(`/rewards/item/${featured.id}`)}
            />
          ) : null}
          {weekly || season ? (
            <div className="rewards-hero-side">
              {weekly ? (
                <PrizeCard
                  label="Weekly prize"
                  title={prizeTitle(weekly, 'Weekly prize')}
                  image={prizeImage(weekly)}
                  status={weeklyStatus}
                  onOpen={() => navigate('/rewards/prize/weekly')}
                />
              ) : null}
              {season ? (
                <PrizeCard
                  label="Season prize"
                  title={prizeTitle(season, 'Season prize')}
                  image={prizeImage(season)}
                  status={seasonStatus}
                  onOpen={() => navigate('/rewards/prize/season')}
                />
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {milestones.length ? (
        <section className="rewards-block" aria-label="Unlock with points">
          <div className="rewards-block-head">
            <h2>Unlock with points</h2>
            <span>You have {formatPoints(availablePoints)} pts</span>
          </div>
          <div className="rewards-unlock">
            {milestones.map((milestone, index) => {
              const cost = milestoneCost(milestone);
              const image = milestoneImage(milestone);
              const isNext = nextLockedIndex === index;
              const locked = !milestone.unlocked && !isNext;
              const canRedeem = !!milestone.unlocked && !milestone.redeemed;
              return (
                <article
                  key={milestone.id || cost}
                  className={`rewards-unlock-card${milestone.id ? ' is-link' : ''}`}
                  {...openProps(milestone.id ? () => navigate(`/rewards/first/${milestone.id}`) : null)}
                >
                  <div className="rewards-unlock-photo">
                    {image ? <img src={image} alt="" /> : null}
                    {locked || isNext ? (
                      <span className="rewards-lock" aria-hidden="true">
                        <svg viewBox="0 0 16 16">
                          <rect x="3" y="7" width="10" height="7" rx="1.5" />
                          <path d="M5.2 7V5.2a2.8 2.8 0 0 1 5.6 0V7" />
                        </svg>
                      </span>
                    ) : null}
                  </div>
                  <p className={`rewards-next${isNext ? '' : ' is-spacer'}`}>{isNext ? 'Next reward' : ''}</p>
                  <h3>{milestone.title}</h3>
                  <p>
                    {isNext
                      ? `${formatPoints(earnedPoints)} / ${formatPoints(cost)} pts`
                      : `${formatPoints(cost)} pts`}
                  </p>
                  <button
                    type="button"
                    className="rewards-redeem"
                    disabled={!canRedeem}
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/rewards/first/${milestone.id}?redeem=1`);
                    }}
                  >
                    {milestone.redeemed ? 'Redeemed' : 'Redeem'}
                  </button>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      {experiences.length ? (
        <section className="rewards-block" aria-label="Experiences">
          <div className="rewards-block-head">
            <h2>Experiences</h2>
            {experiences.length > EXPERIENCE_PREVIEW ? (
              <button type="button" onClick={() => setShowExperiences((open) => !open)}>
                {showExperiences ? 'Show less' : 'View all'}
              </button>
            ) : null}
          </div>
          <div className="rewards-experiences">
            {experienceCards.map((reward) => {
              const start = parseRewardDate(rewardStart(reward));
              const end = parseDateEndOfDay(rewardEnd(reward));
              const locked = start && start.getTime() > now;
              const soon = !locked && end && end.getTime() - now > 0 && end.getTime() - now <= SOON_MS;
              const badge = locked ? unlockLabel(start.getTime(), now) : soon ? 'Ends soon' : '';
              const price = money(reward.cash_price_cents ?? reward.cashPriceCents) || `${formatPoints(reward.points_cost ?? reward.pointsCost)} pts`;
              return (
                <article key={reward.id} className="rewards-exp is-link" {...openProps(() => navigate(`/rewards/item/${reward.id}`))}>
                  {rewardImage(reward) ? <img src={rewardImage(reward)} alt="" /> : <span className="rewards-prize-ph" />}
                  {badge ? <span className="rewards-exp-badge">{badge}</span> : null}
                  <span className="rewards-exp-price">{price}</span>
                  <span className="rewards-exp-copy">
                    <strong>{reward.title}</strong>
                    {reward.description ? <em>{reward.description}</em> : null}
                  </span>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      {merch.length ? (
        <section className="rewards-block" aria-label="Merch">
          <div className="rewards-block-head">
            <h2>Merch</h2>
            {merch.length > MERCH_PREVIEW ? (
              <button type="button" onClick={() => setShowMerch((open) => !open)}>
                {showMerch ? 'Show less' : 'View all'}
              </button>
            ) : null}
          </div>
          <div className="rewards-merch">
            {merchCards.map((reward) => (
                <article key={reward.id} className="rewards-merch-card is-link" {...openProps(() => navigate(`/rewards/item/${reward.id}`))}>
                  <span className="rewards-merch-photo">
                    {rewardImage(reward) ? <img src={rewardImage(reward)} alt="" /> : null}
                  </span>
                  <span className="rewards-merch-copy">
                    <strong>{reward.title}</strong>
                    <span>{formatPoints(reward.points_cost ?? reward.pointsCost)} pts</span>
                    <button type="button">View item</button>
                  </span>
                </article>
            ))}
          </div>
        </section>
      ) : null}

      {raffles.length ? (
        <section className="rewards-block" aria-label="This month's raffles">
          <div className="rewards-block-head">
            <h2>This month&apos;s raffles</h2>
            {raffles.length > RAFFLE_PREVIEW ? (
              <button type="button" onClick={() => setShowRaffles((open) => !open)}>
                {showRaffles ? 'Show less' : 'View all'}
              </button>
            ) : null}
          </div>
          <div className="rewards-raffles">
            {raffleCards.map((reward) => {
              const start = parseRewardDate(rewardStart(reward));
              const waiting = start && start.getTime() > now;
              const cost = Number(reward.points_cost ?? reward.pointsCost) || 0;
              const readyToEnter = !waiting && !reward.is_locked && availablePoints >= cost && windowOpen(reward, now);
              return (
                <article key={reward.id} className="rewards-raffle is-link" {...openProps(() => navigate(`/rewards/item/${reward.id}`))}>
                  <span className="rewards-raffle-photo">
                    {rewardImage(reward) ? <img src={rewardImage(reward)} alt="" /> : null}
                  </span>
                  <strong>{reward.title}</strong>
                  <button
                    type="button"
                    className={readyToEnter ? 'is-ready' : ''}
                    disabled={!readyToEnter}
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/rewards/item/${reward.id}?enter=1`);
                    }}
                  >
                    {readyToEnter ? (
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M2 5.2h12v1.7a1.3 1.3 0 0 0 0 2.2V11a1.2 1.2 0 0 1-1.2 1.2H3.2A1.2 1.2 0 0 1 2 11V9.1a1.3 1.3 0 0 0 0-2.2V5.2z" />
                      </svg>
                    ) : null}
                    {waiting ? opensLabel(rewardStart(reward)) : `Enter for ${formatPoints(cost)} pts`}
                  </button>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      {ready && !hasCatalog ? (
        <p className="rewards-empty">
          {user?.active_school?.name
            ? `No rewards are available for ${user.active_school.name} yet.`
            : 'No rewards are available yet.'}
        </p>
      ) : null}
    </div>
  );
}

function RewardsGate() {
  if (!apiService.isAuthenticated()) return <TempStart />;
  return (
    <AppShell title="Rewards">
      <RewardsPage />
    </AppShell>
  );
}

export default RewardsGate;
